import { Button, Modal } from '@prio365/prio365-react-library';
import { VirtualListItemOnRowProps } from '@prio365/prio365-react-library/lib/VirtualList/components/VirtualListItem';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import {
  getProjectDistributionLists,
  RootReducerState,
} from '../../../../../apps/main/rootReducer';
import {
  ProjectDistributionContact,
  ProjectDistributionList,
} from '../../../../../models/ProjectDistributionList';
import { ProjectId } from '../../../../../models/Types';
import { PrioTheme } from '../../../../../theme/types';
import { makePrioStyles } from '../../../../../theme/utils';
import { fetchProjectDistribustionList } from '../../../../projects/actions';
import ProjectDistributionListTable from '../../../../projects/components/ProjectDistributionListTable';

const useStyles = makePrioStyles((theme: PrioTheme) => ({
  modal: {
    '& .ant-modal-content, & .prio-modal-content': {
      maxHeight: '60vh',
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
    },
  },
  distributionList: {
    height: 475,
    padding: theme.spacing.regular,
  },
}));

interface DistributionListButtonProps {
  value: string[];
  onChange: (emails: string[]) => void;
  projectId: ProjectId;
}

const DistributionListButton: React.FC<DistributionListButtonProps> = ({
  value,
  onChange,
  projectId,
}) => {
  //#region ------------------------------ Defaults
  const classes = useStyles();
  const { t } = useTranslation();
  const dispatch = useDispatch();
  //#endregion

  //#region ------------------------------ States / Attributes / Selectors
  const projectDistributionLists = useSelector<
    RootReducerState,
    ProjectDistributionList[]
  >((state) => getProjectDistributionLists(state, projectId));

  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [selectedDistributionLists, setSelectedDistributionLists] = useState<
    ProjectDistributionList[]
  >([]);
  const [selectedEmails, setSelectedEmails] = useState<string[]>([]);
  //#endregion

  //#region ------------------------------ Methods / Handlers
  const handleOpen = useCallback(() => {
    dispatch(fetchProjectDistribustionList(projectId));
    setSelectedDistributionLists([]);
    setSelectedEmails([]);
    setModalVisible(true);
  }, [dispatch, projectId]);

  const handleOk = useCallback(() => {
    const merged = selectedEmails
      .filter(
        (email, index, self) =>
          index === self.findIndex((e) => e === email) &&
          !value.find((v) => v === email)
      )
      .concat(value);
    onChange(merged);
    setModalVisible(false);
  }, [selectedEmails, value, onChange]);

  const handleCancel = useCallback(() => {
    setSelectedDistributionLists([]);
    setSelectedEmails([]);
    setModalVisible(false);
  }, []);

  const handleOnRow: (
    item: ProjectDistributionList,
    index: number,
    selectedItems: ProjectDistributionList[]
  ) => VirtualListItemOnRowProps<null, null> = useCallback(
    (item, index, selectedItems) => {
      const isSelected = selectedItems.find(
        (i) => i.projectDistributionListId === item.projectDistributionListId
      );
      return {
        onClick: (event) => {
          event.preventDefault();
          setSelectedDistributionLists(
            isSelected
              ? selectedItems.filter(
                  (i) =>
                    i.projectDistributionListId !==
                    item.projectDistributionListId
                )
              : [item, ...selectedItems]
          );
        },
      };
    },
    []
  );
  //#endregion

  //#region ------------------------------ Effects
  useEffect(() => {
    const contacts: ProjectDistributionContact[] = [].concat(
      ...selectedDistributionLists.map(
        (list) => list.projectDistributionContacts
      )
    );
    setSelectedEmails(contacts.map((contact) => contact.eMail));
  }, [selectedDistributionLists]);
  //#endregion

  return (
    <>
      <Button
        type="default"
        onClick={handleOpen}
        iconProp={['fal', 'clipboard-list']}
      />
      <Modal
        title={t('mail:modals.distributionLists.title')}
        okText={t('mail:modals.distributionLists.okText')}
        cancelText={t('mail:modals.distributionLists.cancelText')}
        visible={modalVisible}
        width={'50%'}
        onOk={handleOk}
        onClose={handleCancel}
        okButtonProps={{ disabled: selectedDistributionLists.length === 0 }}
        className={classes.modal}
      >
        <ProjectDistributionListTable
          distributionLists={projectDistributionLists}
          projectId={projectId}
          setSelectedTableItems={setSelectedDistributionLists}
          selectedTableItems={selectedDistributionLists}
          onRowClick={handleOnRow}
          className={classes.distributionList}
          showBulkActions={false}
          showCreateButton={false}
          showDeleteButton={false}
        />
      </Modal>
    </>
  );
};

export default DistributionListButton;
